import type { MenuItem, MenuItemAction } from "@velizhanin-dev/bot-scenario"
import { ArrowDown, ArrowUp, CornerDownRight, Pencil, Plus, Trash2 } from "lucide-react"
import { plural } from "@/entities/audience"
import { Badge } from "@/shared/ui/badge"
import { Button } from "@/shared/ui/button"
import { StyleDot } from "./button-style"
import { AnimatePresence, motion } from "framer-motion"
import { listItem, TRANSITION } from "@/shared/lib/motion"

/** Дерево пунктов меню: рекурсивный рендер, вся логика правок, у вызывающего. */
export function MenuTree({
	items,
	depth,
	actionLabels,
	flowName,
	onMove,
	onEdit,
	onDelete,
	onAddChild,
}: {
	items: MenuItem[]
	depth: number
	actionLabels: Record<MenuItemAction, string>
	flowName: (flowId?: string) => string
	onMove: (id: string, delta: -1 | 1) => void
	onEdit: (item: MenuItem) => void
	onDelete: (item: MenuItem) => void
	onAddChild: (parentId: string) => void
}) {
	return (
		// Пункт добавляется и удаляется по высоте, а при перестановке доезжает до нового места.
		// Отступ между пунктами внутри пункта: у списка его нет, иначе он пропадал бы рывком.
		<ul className={depth > 0 ? "mt-2 border-l border-border/60 pl-4" : undefined}>
			<AnimatePresence initial={false}>
				{items.map((item, index) => (
					<motion.li
						key={item.id}
						layout="position"
						{...listItem}
						transition={{ layout: TRANSITION.slow }}
					>
						<div className="pb-2">
							<div className="flex items-center gap-2 rounded-2xl border border-border bg-card px-3 py-2">
								{depth > 0 ? (
									<CornerDownRight className="h-4 w-4 shrink-0 text-muted-foreground" />
								) : null}
								<div className="min-w-0 flex-1">
									<p className="flex items-center gap-2 truncate text-sm font-medium">
										<StyleDot style={item.style} />
										{item.label}
									</p>
									<p className="truncate text-xs text-muted-foreground">
										<Badge variant="outline" className="mr-1">
											{actionLabels[item.action]}
										</Badge>
										{item.action === "flow"
											? flowName(item.flowId)
											: item.action === "submenu"
												? `${item.items?.length ?? 0} ${plural(item.items?.length ?? 0, ["пункт", "пункта", "пунктов"])}`
												: item.action === "url" || item.action === "web_app"
													? (item.url ?? "")
													: (item.text ?? "").slice(0, 80)}
									</p>
								</div>
								<Button
									variant="ghost"
									size="icon"
									aria-label="Выше"
									disabled={index === 0}
									onClick={() => onMove(item.id, -1)}
								>
									<ArrowUp />
								</Button>
								<Button
									variant="ghost"
									size="icon"
									aria-label="Ниже"
									disabled={index === items.length - 1}
									onClick={() => onMove(item.id, 1)}
								>
									<ArrowDown />
								</Button>
								<Button
									variant="ghost"
									size="icon"
									aria-label="Изменить"
									onClick={() => onEdit(item)}
								>
									<Pencil />
								</Button>
								<Button
									variant="ghost"
									size="icon"
									aria-label="Удалить"
									onClick={() => onDelete(item)}
								>
									<Trash2 />
								</Button>
							</div>
							{item.action === "submenu" ? (
								<>
									<MenuTree
										items={item.items ?? []}
										depth={depth + 1}
										actionLabels={actionLabels}
										flowName={flowName}
										onMove={onMove}
										onEdit={onEdit}
										onDelete={onDelete}
										onAddChild={onAddChild}
									/>
									<Button
										variant="ghost"
										size="sm"
										className="mt-1 ml-4"
										onClick={() => onAddChild(item.id)}
									>
										<Plus />
										Пункт в подменю
									</Button>
								</>
							) : null}
						</div>
					</motion.li>
				))}
			</AnimatePresence>
		</ul>
	)
}
